import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { View } from '../types';
import { stopAllSpeaking } from '../lib/speaking';
import { unlockGameNow } from '../lib/gameLock';
import { resetMascotStreak } from '../lib/mascotReaction';
import { hashToView, viewToHash } from '../lib/viewUrl';

// Each frame remembers whether it was opened via a morphed tile tap, so
// popping back off it can reverse the same "genie" transition (see
// ViewTransition.tsx / Tile.tsx / HomeView.tsx).
type Frame = { view: View; morph: boolean };

// La pila entera viaja en `history.state` de cada entrada del historial. Así
// el atrás/adelante del navegador (o el de Android, o recargar) sabe a qué
// pila volver, y la URL muestra la vista de arriba (ver lib/viewUrl.ts).
//
// Una sola dirección de verdad: "atrás" y "home" desde la app NO tocan la
// pila, le piden al navegador que retroceda y el `popstate` la actualiza.
// Si cada uno la cambiara por su lado, historial y pila se desincronizarían
// al primer atrás del navegador.
type NavState = { clarisaNav: Frame[] };

const sameView = (a: View, b: View) => JSON.stringify(a) === JSON.stringify(b);

const urlFor = (view: View) =>
  `${window.location.pathname}${window.location.search}${viewToHash(view)}`;

function historyStack(): Frame[] | null {
  const s = (window.history.state as Partial<NavState> | null)?.clarisaNav;
  return Array.isArray(s) && s.length > 0 ? s : null;
}

function initialStack(initial: View): Frame[] {
  const base: Frame[] = [{ view: initial, morph: false }];
  if (typeof window === 'undefined') return base;
  const fromHash = hashToView(window.location.hash) ?? initial;
  // Recarga: la entrada del historial sobrevive con su pila. Se usa solo si
  // coincide con la URL, por si alguien editó el hash a mano antes de recargar.
  const saved = historyStack();
  if (saved && sameView(saved[saved.length - 1].view, fromHash)) return saved;
  // Link directo (`#/cuento-reader/3`): la vista arriba del home, así el
  // atrás vuelve al home en vez de salir de la app.
  if (sameView(fromHash, initial)) return base;
  return [...base, { view: fromHash, morph: false }];
}

export function useNav(initial: View = { name: 'home' }, onMorphPop?: () => void) {
  const [stack, setStack] = useState<Frame[]>(() => initialStack(initial));
  const stackRef = useRef(stack);
  stackRef.current = stack;
  const onMorphPopRef = useRef(onMorphPop);
  onMorphPopRef.current = onMorphPop;
  const dirRef = useRef<'push' | 'pop'>('push');
  const morphRef = useRef(false);
  const poppedFromRef = useRef<View | null>(null);

  const view = stack[stack.length - 1].view;
  const canMorphBack = stack[stack.length - 1].morph;

  const updateStack = (updater: (s: Frame[]) => Frame[], direction: 'push' | 'pop', morph = false) => {
    // Cambiar de vista siempre corta lo que esté sonando — grabación, TTS,
    // video, música — sea cual sea el camino por el que estaba sonando. Un
    // juego que se olvida de cortar al desmontarse (o el "¡Muy bien!" de
    // celebrate(), que no vive en ningún componente) dejaba el sonido de la
    // pantalla anterior sonando encima de la nueva; ahora la única manera de
    // cortarlo es, justamente, salir de la sección. Y si estaba frizada por
    // un festejo, no tiene sentido seguir frizada una pantalla que ya no se ve.
    stopAllSpeaking();
    unlockGameNow();
    resetMascotStreak();
    dirRef.current = direction;
    morphRef.current = morph;
    if (typeof document !== 'undefined' && 'startViewTransition' in document) {
      try {
        document.documentElement.dataset.navDir = direction;
        document.documentElement.dataset.navMorph = morph ? 'true' : 'false';
        const transition = (document as any).startViewTransition(() => {
          flushSync(() => {
            setStack(updater);
          });
        });
        transition.finished?.finally?.(() => {
          delete document.documentElement.dataset.navDir;
          delete document.documentElement.dataset.navMorph;
        });
      } catch {
        setStack(updater);
      }
    } else {
      setStack(updater);
    }
  };

  // Mueve la pila a `target` venga de donde venga el cambio (atrás de la app,
  // del navegador, adelante, un hash editado a mano).
  const goTo = (target: Frame[]) => {
    const current = stackRef.current;
    if (target.length < current.length) {
      // Sacar exactamente una vista abierta con morph la devuelve a su tile;
      // saltar varias de golpe (home) es un pop normal, como antes.
      const leaving = current[current.length - 1];
      const morph = leaving.morph && target.length === current.length - 1;
      poppedFromRef.current = morph ? leaving.view : null;
      if (morph) onMorphPopRef.current?.();
      updateStack(() => target, 'pop', morph);
    } else {
      poppedFromRef.current = null;
      updateStack(() => target, 'push');
    }
  };

  // Deja el historial describiendo la pila inicial. Idempotente: en
  // StrictMode corre dos veces y la segunda ve que ya está.
  useEffect(() => {
    const s = stackRef.current;
    const saved = historyStack();
    if (saved && saved.length === s.length && sameView(saved[saved.length - 1].view, s[s.length - 1].view)) return;
    window.history.replaceState({ clarisaNav: s.slice(0, 1) } satisfies NavState, '', urlFor(s[0].view));
    for (let i = 1; i < s.length; i++) {
      window.history.pushState({ clarisaNav: s.slice(0, i + 1) } satisfies NavState, '', urlFor(s[i].view));
    }
  }, []);

  useEffect(() => {
    const onPop = () => {
      const current = stackRef.current;
      let target = historyStack();
      if (!target) {
        // Entrada sin pila: alguien escribió el hash a mano. Si describe una
        // vista, se abre arriba de la actual; si no, se queda donde estaba.
        const typed = window.location.hash ? hashToView(window.location.hash) : initial;
        const top = current[current.length - 1].view;
        if (!typed || sameView(typed, top)) {
          window.history.replaceState({ clarisaNav: current } satisfies NavState, '', urlFor(top));
          return;
        }
        target = sameView(typed, initial) ? current.slice(0, 1) : [...current, { view: typed, morph: false }];
        window.history.replaceState({ clarisaNav: target } satisfies NavState, '', urlFor(typed));
      }
      goTo(target);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Si el historial no tiene nuestra pila (no debería pasar), retroceder por
  // el navegador podría sacarte de la app: mejor cambiar la pila a mano.
  const historyInSync = () => historyStack()?.length === stackRef.current.length;

  const push = useCallback((next: View, opts?: { morph?: boolean }) => {
    const morph = opts?.morph ?? false;
    const target = [...stackRef.current, { view: next, morph }];
    window.history.pushState({ clarisaNav: target } satisfies NavState, '', urlFor(next));
    updateStack(() => target, 'push', morph);
  }, []);

  const pop = useCallback(() => {
    const current = stackRef.current;
    if (current.length <= 1) return;
    if (historyInSync()) {
      window.history.back();
      return;
    }
    const target = current.slice(0, -1);
    window.history.replaceState({ clarisaNav: target } satisfies NavState, '', urlFor(target[target.length - 1].view));
    goTo(target);
  }, []);

  const replace = useCallback((next: View) => {
    poppedFromRef.current = null;
    const target = [...stackRef.current.slice(0, -1), { view: next, morph: false }];
    window.history.replaceState({ clarisaNav: target } satisfies NavState, '', urlFor(next));
    updateStack(() => target, 'push');
  }, []);

  const home = useCallback(() => {
    const current = stackRef.current;
    if (current.length <= 1) return;
    if (historyInSync()) {
      window.history.go(-(current.length - 1));
      return;
    }
    const target = current.slice(0, 1);
    window.history.replaceState({ clarisaNav: target } satisfies NavState, '', urlFor(target[0].view));
    goTo(target);
  }, []);

  return {
    view,
    push,
    pop,
    replace,
    home,
    depth: stack.length,
    dir: dirRef.current,
    morph: morphRef.current,
    canMorphBack,
    poppedFrom: poppedFromRef.current,
  };
}
